import { buildAtlAttributionHeaders } from '../../../common/utils/atl-attribution';
import { type AggResponse, fetchAgg } from '../../../common/utils/fetch-agg';
import { getProductAndIDFromARI } from '../../utils/ari';
import { createCacheKey, createSearchChatCacheKey } from './create-cache-key';
import { intentDetectionCache } from './intent-detection-cache';
import { getCachedSearchChatIntentDetectionResponse } from './search-chat-intent-detection-cache';
import type {
	JiraIntentDetectionQueryVariables,
	KnowledgeDiscoveryIntentDetectionResponse,
	KnowledgeDiscoverySearchChatIntentDetectionResponse,
	SearchChatIntentDetectionQueryVariables,
} from './types';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export { intentDetectionCache } from './intent-detection-cache';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { createCacheKey } from './create-cache-key';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { isIntentDetectionSuccess } from './is-intent-detection-success';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { isIntentDetectionError } from './is-intent-detection-error';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export {
	KnowledgeDiscoverySearchChatIntentDetectionModelVersion,
	type KnowledgeDiscoverySearchChatIntentDetectionModelVersionType,
} from './knowledge-discovery-search-chat-intent-detection-model-version';

const GQL_KNOWLEDGE_DISCOVERY_INTENT_OPERATION_NAME = 'KnowledgeDiscoveryJiraIntentDetectionQuery';
const GQL_SEARCH_CHAT_INTENT_OPERATION_NAME = 'KnowledgeDiscoverySearchChatIntentDetectionQuery';

/**
 * GraphQL query string for Jira intent detection.
 */
const jiraIntentDetectionQueryString = `query ${GQL_KNOWLEDGE_DISCOVERY_INTENT_OPERATION_NAME}(
	$siteId: String!
	$orgId: String!
	$query: String!
	$experience: KnowledgeDiscoveryJiraSearchExperience!
	$model: KnowledgeDiscoveryJiraIntentDetectionModel!
	$sessionId: String
	$includePatternType: Boolean!
) {
	knowledgeDiscovery {
		jiraIntentDetection(
			siteId: $siteId
			orgId: $orgId
			query: $query
			experience: $experience
			model: $model
			sessionId: $sessionId
		) @optIn(to: "KnowledgeDiscovery Jira Intent Detection") {
			__typename
			... on KnowledgeDiscoveryJiraIntentDetection {
				searchClassification {
					type
					pattern @include(if: $includePatternType)
				}
			}
			... on QueryError {
				message
			}
		}
	}
}`;

/** GraphQL query string for Search/Chat convergence intent detection. */
const searchChatIntentDetectionQueryString = `query ${GQL_SEARCH_CHAT_INTENT_OPERATION_NAME}(
	$siteId: String!
	$query: String!
	$locale: String!
	$modelVersion: KnowledgeDiscoverySearchChatIntentDetectionModelVersion!
) {
	knowledgeDiscovery {
		searchChatIntentDetection(
			siteId: $siteId
			query: $query
			locale: $locale
			modelVersion: $modelVersion
		) @optIn(to: "KnowledgeDiscovery Search Chat Intent Detection") {
			__typename
			... on KnowledgeDiscoverySearchChatIntentDetectionSuccess {
				decision
				modelVersion
			}
			... on QueryError {
				message
			}
		}
	}
}`;

/**
 * Query the Knowledge Discovery API for Jira search intent detection.
 *
 * @param variables - Query variables including siteId, orgId, query, experience, and optional model.
 * @returns Promise resolving to the intent detection result.
 * @throws Promise rejection with GraphQL errors if the query fails.
 */
export const jiraIntentDetectionQuery: (
	variables: JiraIntentDetectionQueryVariables,
	headers?: Record<string, string>,
) => Promise<AggResponse<KnowledgeDiscoveryIntentDetectionResponse>> = async (
	variables: JiraIntentDetectionQueryVariables,
	headers?: Record<string, string>,
): Promise<AggResponse<KnowledgeDiscoveryIntentDetectionResponse>> => {
	const cacheKey = createCacheKey(variables);

	return intentDetectionCache.inMemoryDecorator(cacheKey, async () => {
		const response = await fetchAgg<
			JiraIntentDetectionQueryVariables,
			KnowledgeDiscoveryIntentDetectionResponse
		>({
			graphQuery: jiraIntentDetectionQueryString,
			variables,
			operationName: GQL_KNOWLEDGE_DISCOVERY_INTENT_OPERATION_NAME,
			headers: {
				'X-ExperimentalApi': 'KnowledgeDiscovery',
				...headers,
			},
		});

		const hasGraphQLErrors = response.errors?.some((error) => error);

		if (hasGraphQLErrors) {
			return Promise.reject(response);
		}

		const result = response.data?.knowledgeDiscovery.jiraIntentDetection;
		if (result?.__typename === 'QueryError') {
			return Promise.reject(response);
		}

		return response;
	});
};

/** Query the Knowledge Discovery API for Search/Chat convergence intent detection. */
export const searchChatIntentDetectionQuery = async (
	variables: SearchChatIntentDetectionQueryVariables,
	options: {
		/** Called only when this invocation starts a fetch; cached and shared requests skip it. */
		onNetworkRequest?: () => void;
	} = {},
): Promise<AggResponse<KnowledgeDiscoverySearchChatIntentDetectionResponse>> => {
	const cacheKey = createSearchChatCacheKey(variables);

	return getCachedSearchChatIntentDetectionResponse(cacheKey, async () => {
		try {
			options.onNetworkRequest?.();
		} catch {
			// Telemetry must not prevent the network request.
		}

		const [product, cloudId] = getProductAndIDFromARI(variables.siteId);
		const response = await fetchAgg<
			SearchChatIntentDetectionQueryVariables,
			KnowledgeDiscoverySearchChatIntentDetectionResponse
		>({
			graphQuery: searchChatIntentDetectionQueryString,
			variables,
			operationName: GQL_SEARCH_CHAT_INTENT_OPERATION_NAME,
			headers: {
				'X-ExperimentalApi': 'KnowledgeDiscovery',
				...buildAtlAttributionHeaders({ cloudId, product }),
			},
		});

		if (response.errors?.some(Boolean)) {
			throw response;
		}

		const result = response.data?.knowledgeDiscovery.searchChatIntentDetection;
		if (result?.__typename !== 'KnowledgeDiscoverySearchChatIntentDetectionSuccess') {
			throw response;
		}

		return response;
	});
};
